// src/scripts/mailer/mailerCsv.js

function clean(value) {
  return String(value ?? "").trim();
}

function headerKey(value) {
  return clean(value)
    .toLowerCase()
    .replace(/^\uFEFF/, "")
    .replace(/[^a-z0-9]+/g, "");
}

function parseBoolean(value, fallback = null) {
  const text = clean(value).toLowerCase();

  if (!text) return fallback;

  if (
    [
      "yes",
      "y",
      "true",
      "1",
      "subscribed",
      "opted in",
      "optedin",
    ].includes(text)
  ) {
    return true;
  }

  if (
    [
      "no",
      "n",
      "false",
      "0",
      "unsubscribed",
      "opted out",
      "optedout",
    ].includes(text)
  ) {
    return false;
  }

  return fallback;
}

function parseTags(value) {
  const text = clean(value);

  if (!text) {
    return [];
  }

  const seen = new Set();
  const result = [];

  for (const part of text.split(/[|;,]+/g)) {
    const tag = clean(part);

    if (!tag) {
      continue;
    }

    const key = tag.toLowerCase();

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(tag);
  }

  return result.slice(0, 25);
}

function normaliseGender(value) {
  const text = clean(value).toLowerCase();

  if (["male", "m", "man", "men"].includes(text)) {
    return "male";
  }

  if (["female", "f", "woman", "women"].includes(text)) {
    return "female";
  }

  return "";
}

function mergeGenderTag(tags, genderValue) {
  const gender = normaliseGender(genderValue);

  const withoutGender = (Array.isArray(tags) ? tags : []).filter((tag) => {
    const text = clean(tag).toLowerCase();

    return !(
      text === "male" ||
      text === "female" ||
      text.startsWith("gender:") ||
      text.startsWith("gender=")
    );
  });

  if (!gender) {
    return withoutGender.slice(0, 25);
  }

  return [...withoutGender, `gender:${gender}`].slice(0, 25);
}

function normaliseDateOfBirth(value) {
  const text = clean(value);

  if (!text) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);

    const date = new Date(
      Date.UTC(
        year,
        month - 1,
        day
      )
    );

    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    ) {
      return text;
    }

    return "";
  }

  const ukMatch = text.match(
    /^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/
  );

  if (!ukMatch) {
    return "";
  }

  const day = Number(ukMatch[1]);
  const month = Number(ukMatch[2]);
  const year = Number(ukMatch[3]);

  const date = new Date(
    Date.UTC(
      year,
      month - 1,
      day
    )
  );

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return [
    String(year).padStart(4, "0"),
    String(month).padStart(2, "0"),
    String(day).padStart(2, "0"),
  ].join("-");
}

export function parseCsvText(text) {
  const source = String(text ?? "");
  const rows = [];

  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    const next = source[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        cell += '"';
        i += 1;
        continue;
      }

      if (char === '"') {
        inQuotes = false;
        continue;
      }

      cell += char;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      row.push(cell);
      cell = "";
      continue;
    }

    if (char === "\n") {
      row.push(cell);
      cell = "";

      if (row.some((value) => clean(value))) {
        rows.push(row);
      }

      row = [];
      continue;
    }

    if (char !== "\r") {
      cell += char;
    }
  }

  row.push(cell);

  if (row.some((value) => clean(value))) {
    rows.push(row);
  }

  return rows;
}

export function csvToGuestObjects(text, options = {}) {
  const rows = parseCsvText(text);

  if (rows.length < 2) {
    throw new Error(
      "The CSV must contain a header row and at least one guest."
    );
  }

  const headers = rows[0].map(headerKey);

  const aliases = {
    email: [
      "email",
      "emailaddress",
      "emailid",
      "emailaddr",
    ],

    firstName: [
      "firstname",
      "first",
      "forename",
      "givenname",
    ],

    lastName: [
      "lastname",
      "last",
      "surname",
      "familyname",
    ],

    phoneNumber: [
      "phonenumber",
      "phone",
      "telephone",
      "telephonenumber",
      "mobile",
      "mobilenumber",
      "contactnumber",
    ],

    dateOfBirth: [
      "dateofbirth",
      "dob",
      "birthdate",
      "birthday",
    ],

    gender: [
      "gender",
      "sex",
    ],

    tags: [
      "tags",
      "tag",
      "groups",
      "group",
      "segment",
      "segments",
    ],

    subscribed: [
      "subscribed",
      "subscription",
      "consent",
      "marketingconsent",
      "emailconsent",
      "optin",
      "marketingoptin",
    ],
  };

  function indexFor(names) {
    for (const name of names) {
      const index = headers.indexOf(name);

      if (index >= 0) {
        return index;
      }
    }

    return -1;
  }

  const indexes = {
    email: indexFor(aliases.email),
    firstName: indexFor(aliases.firstName),
    lastName: indexFor(aliases.lastName),
    phoneNumber: indexFor(aliases.phoneNumber),
    dateOfBirth: indexFor(aliases.dateOfBirth),
    gender: indexFor(aliases.gender),
    tags: indexFor(aliases.tags),
    subscribed: indexFor(aliases.subscribed),
  };

  if (indexes.email < 0) {
    throw new Error(
      'No email column was found. Use a heading such as "Email" or "Email Address".'
    );
  }

  const forceSubscribed = options.forceSubscribed === true;
  const seen = new Set();
  const guests = [];
  const invalid = [];
  let duplicates = 0;

  rows.slice(1).forEach((raw, rowIndex) => {
    const email = clean(raw[indexes.email]).toLowerCase();

    if (
      !email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      invalid.push({
        row: rowIndex + 2,
        email,
      });

      return;
    }

    if (seen.has(email)) {
      duplicates += 1;
      return;
    }

    seen.add(email);

    const explicitSubscription =
      indexes.subscribed >= 0
        ? parseBoolean(
            raw[indexes.subscribed],
            null
          )
        : null;

    const parsedTags =
      indexes.tags >= 0
        ? parseTags(raw[indexes.tags])
        : [];

    const genderValue =
      indexes.gender >= 0
        ? raw[indexes.gender]
        : "";

    guests.push({
      first_name:
        indexes.firstName >= 0
          ? clean(raw[indexes.firstName])
          : "",

      last_name:
        indexes.lastName >= 0
          ? clean(raw[indexes.lastName])
          : "",

      phone_number:
        indexes.phoneNumber >= 0
          ? clean(raw[indexes.phoneNumber])
          : "",

      email,

      date_of_birth:
        indexes.dateOfBirth >= 0
          ? normaliseDateOfBirth(raw[indexes.dateOfBirth])
          : "",

      /*
       * functions/mailer.js already stores guest tags, so Gender is encoded
       * as gender:male / gender:female. This gives us filtering without
       * altering the Cloud Function or database schema.
       */
      tags: mergeGenderTag(parsedTags, genderValue),

      subscribed:
        explicitSubscription === false
          ? false
          : explicitSubscription === true
          ? true
          : forceSubscribed,

      source: "csv",
    });
  });

  return {
    guests,
    invalid,
    duplicates,
    headers: rows[0],
  };
}

function csvEscape(value) {
  const text = String(value ?? "");

  if (!/[",\n\r]/.test(text)) {
    return text;
  }

  return `"${text.replaceAll(
    '"',
    '""'
  )}"`;
}

export function downloadGuestCsvExample() {
  const content = [
    [
      "First Name",
      "Last Name",
      "Phone Number",
      "Email",
      "Date of Birth",
      "Gender",
    ],

    [
      "Alex",
      "Taylor",
      "07123456789",
      "guest@example.com",
      "14/02/1990",
      "Male",
    ],

    [
      "Sam",
      "Jones",
      "07987654321",
      "another@example.com",
      "03/11/1988",
      "Female",
    ],
  ]
    .map((row) => row.map(csvEscape).join(","))
    .join("\r\n");

  const blob = new Blob(
    [content],
    {
      type: "text/csv;charset=utf-8",
    }
  );

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = "coredeck-mailer-import-example.csv";

  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}
